/**
 * O único lugar do painel que fala HTTP.
 *
 * Três coisas moram aqui, e as três existem porque espalhá-las custaria caro:
 *
 * 1. **O token nunca é montado à mão numa tela.** Uma requisição sem `Authorization` não
 *    falha de forma visível — ela volta 401, o painel desloga a pessoa, e o defeito vira
 *    "às vezes ele me joga para fora".
 *
 * 2. **A renovação acontece uma vez, e não uma por requisição em voo.** Sem a promessa
 *    compartilhada, uma tela com seis consultas dispara seis renovações simultâneas; cinco
 *    delas usam um refresh já rotacionado (o servidor mata o token usado quando emite o
 *    novo), e o resultado é a pessoa ser deslogada justamente quando a tela tem mais dados.
 *
 * 3. **O erro do servidor chega como erro tipado**, com o `code` do catálogo. A tela decide
 *    o que dizer; ela não precisa saber que houve um `ProblemDetail` no meio.
 */

import { lerSessao, gravarSessao, limparSessao } from './tokenStore';

/** O corpo de erro do servidor — `ProblemDetail` com os campos do catálogo (ADR-026). */
export interface ErroDoServidor {
  code: string;
  title: string;
  detail: string;
  status: number;
  recoverable?: boolean;
  appAction?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, mensagem: string) {
    super(mensagem);
    this.status = status;
    this.code = code;
  }
}

/** Renovação em curso, compartilhada por todas as requisições que esbarrarem no 401. */
let renovando: Promise<string | null> | null = null;

async function renovar(): Promise<string | null> {
  const sessao = lerSessao();
  if (!sessao?.refreshToken) {
    return null;
  }
  const r = await fetch('/api/v1/auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: sessao.refreshToken }),
  });
  if (!r.ok) {
    limparSessao();
    return null;
  }
  const novo = await r.json();
  gravarSessao({ ...sessao, accessToken: novo.accessToken, refreshToken: novo.refreshToken });
  return novo.accessToken as string;
}

async function renovarUmaVezSo(): Promise<string | null> {
  if (!renovando) {
    renovando = renovar().finally(() => {
      renovando = null;
    });
  }
  return renovando;
}

async function bruto(caminho: string, init: RequestInit, token: string | null): Promise<Response> {
  const headers = new Headers(init.headers);
  // FormData é a exceção: quem escreve o `Content-Type` dele é o navegador, porque só ele
  // conhece a fronteira que acabou de sortear. Escrever `application/json` aqui produziria
  // um corpo que o servidor não separa.
  if (init.body !== undefined && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(`/api/v1${caminho}`, { ...init, headers });
}

async function pedir<T>(caminho: string, init: RequestInit = {}): Promise<T> {
  const sessao = lerSessao();
  let r = await bruto(caminho, init, sessao?.accessToken ?? null);

  if (r.status === 401 && sessao?.refreshToken) {
    const novo = await renovarUmaVezSo();
    if (novo) {
      r = await bruto(caminho, init, novo);
    }
  }

  if (r.status === 401) {
    limparSessao();
    throw new ApiError(401, 'UNAUTHENTICATED', 'Sessão expirada. Entre de novo.');
  }

  if (!r.ok) {
    let corpo: Partial<ErroDoServidor> = {};
    try {
      corpo = await r.json();
    } catch {
      // Resposta sem corpo JSON — 500 de infraestrutura, por exemplo.
    }
    throw new ApiError(r.status, corpo.code ?? 'INTERNAL',
      corpo.detail ?? 'Não foi possível concluir a operação.');
  }

  if (r.status === 204 || r.headers.get('content-length') === '0') {
    return undefined as T;
  }
  return (await r.json()) as T;
}

/**
 * Baixa um arquivo pela MESMA porta das outras chamadas.
 *
 * Existe para não repetir aqui o que o começo deste arquivo diz que não pode ser repetido:
 * montar `Authorization` numa tela. Um `<a href>` direto para a rota iria sem token, voltaria
 * 401, e o defeito apareceria como "às vezes o download não funciona".
 *
 * O nome do arquivo vem do `Content-Disposition` do servidor, e não é inventado aqui: é ele
 * que carrega o código do lote, e três arquivos `download.csv` na mesma pasta é a confusão
 * que a exportação existe para evitar.
 */
async function baixar(caminho: string): Promise<void> {
  const sessao = lerSessao();
  let r = await bruto(caminho, {}, sessao?.accessToken ?? null);

  if (r.status === 401 && sessao?.refreshToken) {
    const novo = await renovarUmaVezSo();
    if (novo) {
      r = await bruto(caminho, {}, novo);
    }
  }
  if (!r.ok) {
    let corpo: Partial<ErroDoServidor> = {};
    try {
      corpo = await r.json();
    } catch {
      // Sem corpo JSON: fica a mensagem genérica abaixo.
    }
    throw new ApiError(r.status, corpo.code ?? 'INTERNAL',
      corpo.detail ?? 'Não foi possível gerar o arquivo.');
  }

  const nome = /filename="([^"]+)"/.exec(r.headers.get('content-disposition') ?? '')?.[1]
    ?? 'exportacao.txt';
  const endereco = URL.createObjectURL(await r.blob());
  const a = document.createElement('a');
  a.href = endereco;
  a.download = nome;
  a.click();
  // Sem revogar, cada exportação deixa o arquivo inteiro preso na memória da aba até ela
  // ser fechada — e um lote de dez mil etiquetas não é pequeno.
  URL.revokeObjectURL(endereco);
}

/**
 * Envia um arquivo — e **não** põe `Content-Type`.
 *
 * O cabeçalho de um multipart carrega a fronteira (`boundary`) que o navegador sorteia ao
 * serializar o `FormData`. Escrevê-lo à mão, como `pedir` faz com o JSON, produz um corpo
 * que o servidor não consegue separar — e o erro chega como "campo `file` ausente", que
 * manda procurar no lugar errado.
 */
async function enviarArquivo<T>(caminho: string, corpo: FormData): Promise<T> {
  return pedir<T>(caminho, { method: 'POST', body: corpo });
}

/**
 * Um endereço local para uma imagem que exige token.
 *
 * `<img src="/api/v1/...">` iria sem `Authorization` — o navegador não conhece a sessão do
 * painel —, voltaria 401, e a galeria mostraria imagem quebrada. Então os bytes vêm por
 * `fetch`, com a mesma renovação de token das outras chamadas, e viram um `blob:`.
 *
 * Quem chama **precisa revogar** o endereço quando a imagem sair da tela: sem isso, cada
 * troca de produto deixa o arquivo inteiro preso na memória da aba.
 */
async function verImagem(caminho: string): Promise<string> {
  const sessao = lerSessao();
  let r = await bruto(caminho, {}, sessao?.accessToken ?? null);

  if (r.status === 401 && sessao?.refreshToken) {
    const novo = await renovarUmaVezSo();
    if (novo) {
      r = await bruto(caminho, {}, novo);
    }
  }
  if (!r.ok) {
    throw new ApiError(r.status, 'INTERNAL', 'Não foi possível carregar a imagem.');
  }
  return URL.createObjectURL(await r.blob());
}

export const api = {
  baixar,
  enviarArquivo,
  verImagem,
  get: <T>(caminho: string) => pedir<T>(caminho),
  post: <T>(caminho: string, corpo?: unknown) =>
    pedir<T>(caminho, { method: 'POST', body: corpo === undefined ? undefined : JSON.stringify(corpo) }),
  put: <T>(caminho: string, corpo?: unknown) =>
    pedir<T>(caminho, { method: 'PUT', body: corpo === undefined ? undefined : JSON.stringify(corpo) }),
  remover: <T>(caminho: string) => pedir<T>(caminho, { method: 'DELETE' }),
};

/**
 * Monta a query string ignorando o que está vazio.
 *
 * Um filtro em branco enviado como `?serial=` faz o servidor procurar por vazio, e a tela
 * volta sem resultado nenhum — com cara de "não existe item".
 */
export function query(params: Record<string, string | number | undefined | null>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      q.set(k, String(v));
    }
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}
